import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/shared/lib/env';
import { useAuthStore } from '@/store/auth.store';
import type { ApiProblem } from './types';

export const api = axios.create({
  baseURL: env.VITE_API_URL,
  withCredentials: true,
  timeout: 30_000,
});

api.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token && cfg.headers) cfg.headers.Authorization = `Bearer ${token}`;
  if (cfg.headers) cfg.headers['X-Correlation-Id'] = crypto.randomUUID();
  return cfg;
});

let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  try {
    const res = await axios.post(
      `${env.VITE_API_URL}/api/v1/auth/refresh`,
      {},
      { withCredentials: true, timeout: 15_000 },
    );
    const data = res.data as { accessToken: string; accessExpiresAtUtc: string; user: any };
    useAuthStore.getState().setSession(data);
    return data.accessToken;
  } catch {
    useAuthStore.getState().clearSession();
    return null;
  }
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError<ApiProblem>) => {
    const cfg = err.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    if (!cfg) return Promise.reject(err);

    const url = cfg.url ?? '';
    if (err.response?.status === 401 && !cfg._retry && !url.includes('/auth/login') && !url.includes('/auth/refresh')) {
      cfg._retry = true;
      refreshing ??= refreshAccessToken().finally(() => {
        refreshing = null;
      });
      const newToken = await refreshing;
      if (newToken) {
        cfg.headers = cfg.headers ?? {};
        (cfg.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
        return api(cfg);
      }
    }
    return Promise.reject(err);
  },
);

export function getErrorMessage(err: unknown): string {
  if (axios.isAxiosError<ApiProblem>(err)) {
    const data = err.response?.data;
    if (data?.errors) {
      const first = Object.values(data.errors)[0]?.[0];
      if (first) return first;
    }
    return data?.detail ?? data?.title ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}
